import {
  Checkbox,
  Input,
  Radio,
  RadioGroup,
  StatusCard,
  TextBody,
  TextCaption,
} from '@pypestream/design-system';
import { valueOf } from '@pypestream/ui-patterns';
import { useEffect, useRef, useState } from 'react';

import {
  type AudienceOrg,
  fetchAudienceCount,
  fetchAudienceOrgs,
  type KeyAudience,
  type KeyAudienceMode,
} from './maestro-org-api';
import { useSecretsText } from './org-secrets-text';

/**
 * Spec 2.3.4–5: All Organizations with some unticked, or only the ticked ones, then how many
 * receive the key. For `all`, `accountIds` are the excluded orgs; for `selected`, the chosen ones.
 */
export function KeyAudiencePicker({
  accountId,
  audience,
  onChange,
  onCount,
}: {
  accountId: string;
  audience: KeyAudience;
  onChange: (audience: KeyAudience) => void;
  /** The confirmed count, or null while it is unknown, so the caller can hold the save. */
  onCount: (count: number | null) => void;
}) {
  const text = useSecretsText();
  const [orgs, setOrgs] = useState<AudienceOrg[] | null>(null);
  const [search, setSearch] = useState('');
  const [count, setCount] = useState<number | 'failed' | null>(null);
  const request = useRef(0);
  const onCountRef = useRef(onCount);
  onCountRef.current = onCount;

  useEffect(() => {
    let current = true;
    fetchAudienceOrgs(accountId).then(
      (loaded) => current && setOrgs(loaded),
      () => current && setOrgs([])
    );
    return () => {
      current = false;
    };
  }, [accountId]);

  // Only the latest answer counts: ticking fast must not leave an older total on screen.
  useEffect(() => {
    const id = ++request.current;
    setCount(null);
    onCountRef.current(null);
    fetchAudienceCount(accountId, audience).then(
      (n) => {
        if (id !== request.current) return;
        setCount(n);
        onCountRef.current(n);
      },
      () => id === request.current && setCount('failed')
    );
  }, [accountId, audience]);

  const ticked = (org: AudienceOrg) =>
    audience.accountIds.includes(org.accountId) ===
    (audience.mode === 'selected');
  const toggle = (org: AudienceOrg) =>
    onChange({
      ...audience,
      accountIds: audience.accountIds.includes(org.accountId)
        ? audience.accountIds.filter((id) => id !== org.accountId)
        : [...audience.accountIds, org.accountId],
    });
  const needle = search.trim().toLowerCase();
  const shown = (orgs ?? []).filter(
    (org) => !needle || org.name.toLowerCase().includes(needle)
  );

  return (
    <>
      <RadioGroup
        label={text('confirmKey.receives')}
        value={audience.mode}
        orientation="vertical"
        onChange={(e) =>
          onChange({ mode: valueOf(e) as KeyAudienceMode, accountIds: [] })
        }
      >
        {(['all', 'selected'] as const).map((mode) => (
          <Radio key={mode} value={mode}>
            {text(`confirmKey.${mode}`)}
            <TextBody size="small" variant="secondary">
              {text(`confirmKey.${mode}Help`)}
            </TextBody>
          </Radio>
        ))}
      </RadioGroup>
      {orgs !== null && orgs.length === 0 ? (
        <TextCaption variant="secondary">
          {text('confirmKey.noOrgs')}
        </TextCaption>
      ) : (
        <>
          <Input
            variant="outlined"
            type="search"
            aria-label={text('confirmKey.search')}
            placeholder={text('confirmKey.search')}
            value={search}
            onInput={(e) => setSearch(valueOf(e))}
          />
          <div className="c-org-audience" data-test-id="org-key-audience">
            {shown.map((org) => (
              <Checkbox
                key={org.accountId}
                checked={ticked(org)}
                test-id={`org-key-audience-${org.accountId}`}
                onChange={() => toggle(org)}
              >
                {org.name}
              </Checkbox>
            ))}
          </div>
        </>
      )}
      <StatusCard
        status={count === 'failed' ? 'error' : 'warning'}
        test-id="org-key-audience-count"
        body={
          count === null
            ? text('confirmKey.counting')
            : count === 'failed'
              ? text('confirmKey.countFailed')
              : text('confirmKey.count', { count })
        }
      />
    </>
  );
}
